// A panel that floats over the 3D stage. It sits where the stylesheet puts it until the
// visitor drags it, then stays where it was dropped, kept fully on the stage. Nothing is
// stored, so a reload puts it back.

const NUDGES = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
const RESIZE_ICON_PATHS = {
  "top-left": "M7 15V7h8",
  "top-right": "M9 7h8v8",
  "bottom-left": "M7 9v8h8",
  "bottom-right": "M6 18 18 6m-7 12 7-7m-2 7 2-2",
};

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}

// `handle` is where a drag may start (the whole card by default). `ignore` is a selector for
// controls inside it that must keep their own clicks. `grip` is an optional focusable element:
// the arrow keys nudge the card (Shift for bigger steps) and Home puts it back.
// Call `place()` after the card is shown, resized, or its stage changes size.
export function floatingCard({ stage, card, handle = card, grip = null, ignore = null, resizable = false, onResize = () => {} }) {
  // Pixels from the stage's top-left corner; null keeps the stylesheet position.
  let offset = null;

  if (resizable) {
    for (const corner of ["top-left", "top-right", "bottom-left", "bottom-right"]) {
      if (card.querySelector(`[data-resize-corner="${corner}"]`)) continue;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "three-d-readout-resize-handle";
      button.dataset.resizeCorner = corner;
      button.setAttribute("aria-label", `Resize water temperature card from the ${corner.replace("-", " ")} corner`);
      const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      icon.setAttribute("viewBox", "0 0 24 24");
      icon.setAttribute("aria-hidden", "true");
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", RESIZE_ICON_PATHS[corner]);
      icon.append(path);
      button.append(icon);
      card.append(button);
    }
  }

  function position() {
    const stageBox = stage.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    return { left: cardBox.left - stageBox.left - stage.clientLeft, top: cardBox.top - stageBox.top - stage.clientTop };
  }

  function place() {
    const { style } = card;
    if (!offset) {
      style.left = "";
      style.top = "";
      style.right = "";
      style.bottom = "";
      return;
    }
    if (card.hidden) return;
    const maxLeft = Math.max(0, stage.clientWidth - card.offsetWidth);
    const maxTop = Math.max(0, stage.clientHeight - card.offsetHeight);
    style.left = `${Math.round(clamp(offset.left, 0, maxLeft))}px`;
    style.top = `${Math.round(clamp(offset.top, 0, maxTop))}px`;
    style.right = "auto";
    style.bottom = "auto";
  }

  // The remembered spot is where the card really is, so dragging or nudging back from an edge responds at once.
  function moveTo(left, top) {
    offset = { left, top };
    place();
    offset = position();
  }

  function resizeStart() {
    const bounds = card.getBoundingClientRect();
    const spot = position();
    return { ...spot, width: bounds.width, height: bounds.height, right: spot.left + bounds.width, bottom: spot.top + bounds.height };
  }

  function resizeFrom(start, corner, deltaX, deltaY) {
    const stageWidth = stage.clientWidth;
    const stageHeight = stage.clientHeight;
    const narrow = stageWidth <= 519;
    const widthGutter = narrow ? 16 : 24;
    const availableWidth = Math.max(0, stageWidth - widthGutter);
    const availableHeight = Math.max(0, stageHeight - 24);
    const style = getComputedStyle(card);
    const pixels = (value) => /^\d+(?:\.\d+)?px$/.test(value.trim()) ? Number.parseFloat(value) : null;
    const minWidth = Math.min(pixels(style.minWidth) ?? (narrow ? 150 : 180), availableWidth);
    const maxWidth = Math.max(minWidth, Math.min(pixels(style.maxWidth) ?? availableWidth, availableWidth,
      corner.endsWith("left") ? start.right : stageWidth - start.left));
    const minHeight = Math.min(pixels(style.minHeight) ?? 72, availableHeight);
    const maxHeight = Math.max(minHeight, Math.min(pixels(style.maxHeight) ?? availableHeight, availableHeight,
      corner.startsWith("top") ? start.bottom : stageHeight - start.top));
    const fromLeft = corner.endsWith("left");
    const fromTop = corner.startsWith("top");
    const width = clamp(start.width + (fromLeft ? -deltaX : deltaX), minWidth, maxWidth);
    const height = clamp(start.height + (fromTop ? -deltaY : deltaY), minHeight, maxHeight);
    const left = fromLeft ? start.right - width : start.left;
    const top = fromTop ? start.bottom - height : start.top;

    card.style.width = `${Math.round(width)}px`;
    card.style.height = `${Math.round(height)}px`;
    offset = { left, top };
    place();
    offset = position();
  }

  function resizeCorner(event) {
    return resizable ? event.target.closest?.("[data-resize-corner]")?.dataset.resizeCorner || null : null;
  }

  function reset() {
    offset = null;
    place();
  }

  const ignored = (event) => Boolean(ignore) && Boolean(event.target.closest?.(ignore));

  handle.addEventListener("pointerdown", (event) => {
    if (event.button !== 0 || ignored(event)) return;
    const corner = resizeCorner(event);
    if (corner) {
      event.preventDefault();
      const start = resizeStart();
      handle.setPointerCapture(event.pointerId);
      card.classList.add("is-resizing");
      const move = (next) => resizeFrom(start, corner, next.clientX - event.clientX, next.clientY - event.clientY);
      const finish = () => {
        card.classList.remove("is-resizing");
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", finish);
        handle.removeEventListener("pointercancel", finish);
        onResize();
      };
      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", finish);
      handle.addEventListener("pointercancel", finish);
      return;
    }
    event.preventDefault();
    const start = position();
    handle.setPointerCapture(event.pointerId);
    card.classList.add("is-dragging");
    const move = (next) => moveTo(start.left + next.clientX - event.clientX, start.top + next.clientY - event.clientY);
    const finish = () => {
      card.classList.remove("is-dragging");
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", finish);
      handle.removeEventListener("pointercancel", finish);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", finish);
    handle.addEventListener("pointercancel", finish);
  });

  handle.addEventListener("dblclick", (event) => {
    if (!ignored(event) && !resizeCorner(event)) reset();
  });

  if (resizable) {
    for (const button of card.querySelectorAll("[data-resize-corner]")) {
      button.addEventListener("keydown", (event) => {
        if (NUDGES[event.key]) {
          event.preventDefault();
          const step = event.shiftKey ? 32 : 8;
          const [x, y] = NUDGES[event.key];
          resizeFrom(resizeStart(), button.dataset.resizeCorner, x * step, y * step);
          onResize();
        }
      });
    }
  }

  grip?.addEventListener("keydown", (event) => {
    if (event.key === "Home") {
      event.preventDefault();
      reset();
    } else if (NUDGES[event.key]) {
      event.preventDefault();
      const step = event.shiftKey ? 64 : 16;
      const current = position();
      moveTo(current.left + NUDGES[event.key][0] * step, current.top + NUDGES[event.key][1] * step);
    }
  });

  return { place, reset };
}
